import {
  Server,
  Globe,
  Clock,
  Cpu,
  MemoryStick,
  Container,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Skull,
  Network,
  Heart,
} from 'lucide-react';
import type { ActiveSprite, FlyMachine } from '../../api/coder';
import { SpriteActivityFeed } from '../sprint/SpriteActivityFeed';

interface SpritesTabProps {
  machines: FlyMachine[];
  sprites: ActiveSprite[];
  flyDashboardUrl: string;
  flyApp: string;
  expandedMachine: string | null;
  onToggleMachine: (machineId: string | null) => void;
  batchId?: string;
}

const regionLabels: Record<string, string> = {
  ord: 'Chicago', iad: 'Ashburn', ewr: 'Secaucus', atl: 'Atlanta',
  lax: 'Los Angeles', sjc: 'San Jose', sea: 'Seattle', dfw: 'Dallas',
  den: 'Denver', mia: 'Miami', yyz: 'Toronto', lhr: 'London',
  ams: 'Amsterdam', fra: 'Frankfurt', cdg: 'Paris', sin: 'Singapore',
  nrt: 'Tokyo', syd: 'Sydney', gru: 'Sao Paulo', bom: 'Mumbai',
};

function formatDuration(seconds: number) {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function stateClasses(state: string): string {
  switch (state) {
    case 'started': return 'bg-accent-100 text-accent-700';
    case 'stopped': return 'bg-amber-100 text-amber-700';
    case 'failed': return 'bg-red-100 text-red-700';
    case 'destroyed': return 'bg-neutral-100 text-neutral-400';
    default: return 'bg-neutral-100 text-neutral-600';
  }
}

export function SpritesTab({ machines, sprites, flyDashboardUrl, flyApp, expandedMachine, onToggleMachine, batchId }: SpritesTabProps) {
  if (machines.length === 0 && sprites.length === 0) {
    return (
      <div className="h-28 flex flex-col items-center justify-center border-2 border-dashed border-neutral-200 rounded-lg">
        <Server className="w-6 h-6 text-neutral-300 mb-1.5" />
        <p className="text-xs text-neutral-400">No sprites</p>
        <p className="text-[9px] text-neutral-300 mt-0.5">Sprites are Fly.io machines spawned during code execution</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Summary bar */}
      {machines.length > 0 && (
        <div className="flex items-center justify-between text-xs text-neutral-500">
          <div className="flex items-center gap-3">
            <span className="font-medium">{machines.length} machine{machines.length !== 1 ? 's' : ''}</span>
            <span className="text-neutral-300">|</span>
            <span className="text-accent-600">
              {machines.filter((m) => m.state === 'started').length} running
            </span>
            {machines.filter((m) => m.state === 'stopped').length > 0 && (
              <>
                <span className="text-neutral-300">|</span>
                <span>{machines.filter((m) => m.state === 'stopped').length} stopped</span>
              </>
            )}
            {machines.filter((m) => m.state === 'destroyed').length > 0 && (
              <>
                <span className="text-neutral-300">|</span>
                <span className="text-neutral-400">
                  {machines.filter((m) => m.state === 'destroyed').length} destroyed
                </span>
              </>
            )}
          </div>
          {flyDashboardUrl && (
            <a
              href={flyDashboardUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-lg hover:bg-coral-50 text-coral-500 transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              Fly.io Dashboard
            </a>
          )}
        </div>
      )}

      {/* Machine cards */}
      {machines.map((machine) => {
        const isExpanded = expandedMachine === machine.machineId;
        const spriteName = machine.spriteName || machine.name.replace(/-sprite-[a-f0-9]+$/, '') || machine.name;
        const machineUrl = flyApp ? `https://fly.io/apps/${flyApp}/machines/${machine.machineId}` : '';

        return (
          <div
            key={machine.machineId}
            className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm overflow-hidden transition-shadow hover:shadow-warm"
            style={{ opacity: machine.state === 'destroyed' ? 0.7 : 1 }}
          >
            {/* Card header */}
            <div
              className="p-3 cursor-pointer"
              onClick={() => onToggleMachine(isExpanded ? null : machine.machineId)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center relative bg-coral-50 text-coral-500">
                    <Server className="w-4 h-4" />
                    {machine.state === 'started' && (
                      <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white bg-accent-500" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-sm font-medium text-neutral-800">{spriteName}</h4>
                    <p className="text-[10px] font-mono text-neutral-400 truncate">{machine.machineId}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {machine.oomKilled && (
                    <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700 flex items-center gap-1">
                      <Skull className="w-3 h-3" /> OOM
                    </span>
                  )}
                  {machine.exitCode !== null && machine.exitCode !== 0 && !machine.oomKilled && (
                    <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">
                      exit {machine.exitCode}
                    </span>
                  )}
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${stateClasses(machine.state)}`}>
                    {machine.state}
                  </span>
                  {isExpanded
                    ? <ChevronUp className="w-3.5 h-3.5 text-neutral-400" />
                    : <ChevronDown className="w-3.5 h-3.5 text-neutral-400" />
                  }
                </div>
              </div>

              {/* Quick stats row */}
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <div className="flex items-center gap-1 text-[11px] text-neutral-600">
                  <Globe className="w-3 h-3 text-coral-400" />
                  <span className="font-mono">{machine.region}</span>
                  {regionLabels[machine.region] && (
                    <span className="text-neutral-400">({regionLabels[machine.region]})</span>
                  )}
                </div>
                <div className="flex items-center gap-1 text-[11px] text-neutral-600">
                  <Clock className="w-3 h-3 text-amber-400" />
                  <span>{formatDuration(machine.lifespanSeconds)}</span>
                </div>
                <div className="flex items-center gap-1 text-[11px] text-neutral-600">
                  <Cpu className="w-3 h-3 text-coral-400" />
                  <span>{machine.config.cpus} {machine.config.cpuKind} vCPU</span>
                </div>
                <div className="flex items-center gap-1 text-[11px] text-neutral-600">
                  <MemoryStick className="w-3 h-3 text-coral-400" />
                  <span>{machine.config.memoryMb} MB</span>
                </div>
                {machine.imageRef && (
                  <div className="flex items-center gap-1 text-[11px] text-neutral-400">
                    <Container className="w-3 h-3" />
                    <span>{machine.imageRef.repository}:{machine.imageRef.tag}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Expanded details */}
            {isExpanded && (
              <div className="border-t border-neutral-100 px-3 pb-3 pt-2.5 space-y-2.5 bg-neutral-50/50">
                {machineUrl && (
                  <a
                    href={machineUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md border border-neutral-200 text-coral-500 transition-colors hover:bg-white"
                  >
                    <ExternalLink className="w-3 h-3" />
                    View on Fly.io
                  </a>
                )}

                {/* IDs grid */}
                <div className="grid grid-cols-3 gap-2.5">
                  <div>
                    <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Machine ID</p>
                    <p className="text-[11px] font-mono text-neutral-700 truncate">{machine.machineId}</p>
                  </div>
                  <div>
                    <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Sprite ID</p>
                    <p className="text-[11px] font-mono text-neutral-700 truncate">{machine.spriteId || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Instance ID</p>
                    <p className="text-[11px] font-mono text-neutral-700 truncate">{machine.instanceId || '—'}</p>
                  </div>
                </div>

                {/* Exit info */}
                {(machine.exitCode !== null || machine.exitedAt) && (
                  <div className="grid grid-cols-3 gap-2.5">
                    <div>
                      <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Exit Code</p>
                      <p className={`text-[11px] font-mono ${machine.exitCode === 0 ? 'text-accent-600' : 'text-red-600'}`}>
                        {machine.exitCode ?? '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">OOM Killed</p>
                      <p className={`text-[11px] ${machine.oomKilled ? 'text-red-600' : 'text-accent-600'}`}>
                        {machine.oomKilled ? 'Yes' : 'No'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Exited At</p>
                      <p className="text-[11px] text-neutral-700">
                        {machine.exitedAt ? new Date(machine.exitedAt).toLocaleString() : '—'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Network & Image */}
                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Private IP</p>
                    <div className="flex items-center gap-1">
                      <Network className="w-3 h-3 text-neutral-400" />
                      <p className="text-[11px] font-mono text-neutral-700">{machine.privateIp || '—'}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Image</p>
                    <div className="flex items-center gap-1">
                      <Container className="w-3 h-3 text-neutral-400" />
                      <p className="text-[11px] font-mono text-neutral-700 truncate">
                        {machine.imageRef
                          ? `${machine.imageRef.repository}:${machine.imageRef.tag}`
                          : machine.config.image || '—'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Timestamps */}
                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Created</p>
                    <p className="text-[11px] text-neutral-700">
                      {machine.createdAt ? new Date(machine.createdAt).toLocaleString() : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Host Status</p>
                    <div className="flex items-center gap-1">
                      <Heart className="w-3 h-3" style={{ color: machine.hostStatus === 'ok' ? '#22C55E' : '#EF4444' }} />
                      <p className={`text-[11px] ${machine.hostStatus === 'ok' ? 'text-accent-600' : 'text-red-600'}`}>
                        {machine.hostStatus}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Events timeline */}
                {machine.events.length > 0 && (
                  <div>
                    <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400 mb-1.5">Events</p>
                    <div className="space-y-0.5">
                      {machine.events.map((event, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-[11px]">
                          <div
                            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                            style={{
                              backgroundColor: event.type === 'start' ? '#52A560'
                                : event.type === 'launch' ? '#D97F6F'
                                : event.type === 'exit' ? '#F59E0B'
                                : event.type === 'destroy' ? '#EF4444'
                                : '#A39686',
                            }}
                          />
                          <span className="font-mono w-14 text-neutral-600">{event.type}</span>
                          <span className="text-neutral-400">{event.status}</span>
                          {event.exitCode !== null && (
                            <span className={`font-mono ${event.exitCode === 0 ? 'text-accent-600' : 'text-red-600'}`}>
                              code={event.exitCode}
                            </span>
                          )}
                          {event.oomKilled && (
                            <span className="font-mono text-red-600">OOM</span>
                          )}
                          <span className="ml-auto font-mono text-neutral-400">
                            {event.timestamp
                              ? new Date(typeof event.timestamp === 'number' ? event.timestamp : event.timestamp).toLocaleTimeString()
                              : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Env vars */}
                {Object.keys(machine.env).length > 0 && (
                  <div>
                    <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400 mb-1.5">Environment</p>
                    <div className="space-y-0.5">
                      {Object.entries(machine.env).map(([key, val]) => (
                        <div key={key} className="flex items-center gap-2 text-[11px] font-mono">
                          <span className="text-neutral-600">{key}</span>
                          <span className="text-neutral-300">=</span>
                          <span className="truncate text-neutral-700">{val || '(empty)'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Live Activity */}
                {batchId && machine.spriteId && (
                  <div>
                    <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400 mb-1.5">Live Activity</p>
                    <SpriteActivityFeed
                      batchId={batchId}
                      filterId={machine.spriteId}
                      filterField="agent_id"
                      maxHeight="180px"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Fallback: Temporal sprites */}
      {machines.length === 0 && sprites.length > 0 && (
        <>
          <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400 mt-3">
            Temporal Sprites (Fly.io data unavailable)
          </p>
          {sprites.map((sprite) => (
            <div
              key={sprite.id}
              className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm p-3"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-coral-50 text-coral-500">
                    <Server className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-neutral-800">{sprite.id}</h4>
                    <p className="text-[11px] text-neutral-500">Epic: {sprite.epicId}</p>
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                  sprite.status === 'running' ? 'bg-accent-100 text-accent-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  {sprite.status}
                </span>
              </div>
              <div className="mt-2 text-[10px] text-neutral-400">
                <span>Workflow: {sprite.workflowId}</span>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
